import React, { ReactNode } from 'react';
import { SxProps, Theme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardHeader, { CardHeaderProps } from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';

const DEFAULT_CARD_STYLE: SxProps<Theme> = {
  borderRadius: 2.5,
};

interface HeaderNodes extends Omit<CardHeaderProps, 'title' | 'subheader' | 'avatar' | 'action'> {
  avatar?: ReactNode;
  title?: ReactNode;
  subheader?: ReactNode;
  action?: ReactNode;
}

interface MediaAttributes {
  mediaComponent?: 'img' | 'video' | 'audio' | string;
  image?: string;
  altText?: string;
  [key: string]: any; // For other media attributes that might be passed
}

interface ActionsNode {
  style?: SxProps<Theme>;
  display: ReactNode;
}

interface GlobalCardProps {
  raised?: boolean;
  style?: SxProps<Theme>;
  headerNodes?: HeaderNodes;
  mediaAttributes?: MediaAttributes;
  actionsNode?: ActionsNode;
  children?: ReactNode;
}

export const GlobalCard: React.FC<GlobalCardProps> = (props) => {
  const { raised = true, style, headerNodes, mediaAttributes, actionsNode, children } = props;
  const { avatar, title, subheader, action, ...headerRest } = headerNodes || {};
  const { mediaComponent = 'img', image, altText = '', ...mediaRest } = mediaAttributes || {};
  const cardStyle = style || DEFAULT_CARD_STYLE;

  return (
    <Card raised={raised} sx={cardStyle}>
      {headerNodes && (
        <CardHeader
          avatar={avatar}
          title={title}
          subheader={subheader}
          action={action}
          {...headerRest}
        />
      )}
      {mediaAttributes && (
        <CardMedia
          component={mediaComponent as any}
          image={image}
          alt={altText}
          {...mediaRest}
        />
      )}
      {children && (
        <CardContent>
          {children}
        </CardContent>
      )}
      {actionsNode && (
        <CardActions sx={actionsNode.style}>
          {actionsNode.display}
        </CardActions>
      )}
    </Card>
  );
}; 